import { type ReactNode, useCallback, useLayoutEffect, useRef } from "react"
import { matchPath, Routes, useLocation, useNavigate, useNavigationType } from "react-router"
import { PortalContainerProvider } from "../hooks/usePortalContainer"
import { TaskCreateOverlay } from "../pages/TaskCreateOverlay"
import { CodeNavigationProvider, codeRoutes, currentLocationPath, getNewTaskReturnPath, getNewTaskRouteState, useCodeNavigate } from "../routing"
import { RouteOverlayProvider } from "./RouteOverlay"

export function TaskCreateRoutes({ children }: { children: ReactNode }) {
    const location = useLocation()
    const navigate = useNavigate()
    const navigationType = useNavigationType()
    const codeNavigate = useCodeNavigate()
    const state = getNewTaskRouteState(location)
    const workspaceId = state ? matchPath(codeRoutes.CodeWorkspaceTaskCreate.path, location.pathname)?.params.workspaceId : undefined
    const overlayState = workspaceId ? state : null
    const latestRoute = useRef({ location, navigate })
    const previousOverlay = useRef(overlayState)
    const priorOverlay = previousOverlay.current

    // Back pops the original background entry. Resolve it to the latest background
    // before rendering, then replace that entry so another Back behaves normally.
    const returnedBackground =
        !overlayState &&
        priorOverlay &&
        navigationType === "POP" &&
        (currentLocationPath(location) === priorOverlay.newTaskReturnPath || getNewTaskReturnPath(location) === priorOverlay.newTaskReturnPath)
            ? priorOverlay
            : null

    useLayoutEffect(() => {
        latestRoute.current = { location, navigate }
        previousOverlay.current = overlayState
        if (returnedBackground && returnedBackground.newTaskBackgroundPath !== currentLocationPath(location)) {
            const existingState: unknown = location.state
            const returnState = typeof existingState === "object" && existingState !== null && !Array.isArray(existingState) ? existingState : {}
            navigate(returnedBackground.newTaskBackgroundPath, {
                replace: true,
                // Keep the return identity after replacing its path so Forward,
                // further background navigation, and another Back still compose.
                state: { ...returnState, newTaskReturnPath: returnedBackground.newTaskReturnPath },
            })
        }
    }, [location, navigate, overlayState, returnedBackground])

    // Delayed callbacks created before the modal opened must use the current layer.
    const navigateBackground = useCallback((path: string) => {
        const current = latestRoute.current
        const currentState = getNewTaskRouteState(current.location)
        if (currentState && matchPath(codeRoutes.CodeWorkspaceTaskCreate.path, current.location.pathname)) {
            current.navigate(currentLocationPath(current.location), {
                replace: true,
                state: { ...currentState, newTaskBackgroundPath: path },
            })
        } else {
            current.navigate(path)
        }
    }, [])

    const overlay = workspaceId ? (
        <PortalContainerProvider portalId="new-task-portal-root">
            <TaskCreateOverlay
                workspaceId={workspaceId}
                onDismiss={codeNavigate.dismissTaskCreate}
                onWorkspaceSelect={codeNavigate.switchTaskCreateWorkspace}
            />
        </PortalContainerProvider>
    ) : null

    return (
        <RouteOverlayProvider overlay={overlay}>
            <div className="contents" data-code-route-background="true">
                <CodeNavigationProvider navigate={navigateBackground}>
                    <Routes location={overlayState?.newTaskBackgroundPath ?? returnedBackground?.newTaskBackgroundPath ?? location}>{children}</Routes>
                </CodeNavigationProvider>
            </div>
        </RouteOverlayProvider>
    )
}
